import React, { useState } from 'react';
import { App } from 'antd';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { attachOrderToTransportRoute, type TransportAttachResult } from './api/transportRoutesApi';
import { getTransportErrorMessage, isBlockingTransportError } from './transportErrorMessages';
import { describeRemainingShares, showTransportSaveWarnings } from './transportSaveWarnings';
import { routeAllocationKeyLabel } from './transportCostSplitPreview';
import { getTransportLabel } from '../../utils/transportLabel';
import { formatMoneyPl } from '../../utils/currency';
import { formatLocalizedPercent } from '../../utils/numberFormatting';
import { useFeedback } from '../../utils/feedback';

/**
 * Usability round 2026-10-05: the „Dołożono …” toasts must not cover the
 * page header after the operator moves on (e.g. „Edytuj auto”). They carry
 * keys, last a limited time and are closed when the edit page opens.
 */
export const ATTACH_FEEDBACK_KEYS = ['transport-attach-success', 'transport-attach-manual'] as const;
export const ATTACH_SUCCESS_DURATION_SECONDS = 12;

export function dismissAttachFeedback(message: { destroy: (key?: React.Key) => void }): void {
  ATTACH_FEEDBACK_KEYS.forEach((key) => message.destroy(key));
}

export interface AttachTargetRef {
  type: 'order' | 'supplierOrder';
  id: string;
  label: string;
}

/**
 * One home for „Dołóż do auta” (decision 2026-10-04, F2): the POST, the
 * success message with the recalculated share, the shared save warnings and
 * the manual-key hint. Used from the order/ZD cards (TransportAttachCandidates)
 * and from the transport list („Dołóż zamówienie”, usability round 2026-10-05).
 */
export function useAttachToTransport(canViewCosts: boolean) {
  const { t, i18n } = useTranslation(['transport', 'common']);
  const { modal } = App.useApp();
  const feedback = useFeedback();
  const [saving, setSaving] = useState(false);

  const describe = (target: AttachTargetRef, result: TransportAttachResult): string => {
    const { route } = result;
    if (target.type === 'order' && route.direction !== 'INBOUND') {
      const share = route.routeOrders.find((row) => row.orderId === target.id);
      if (canViewCosts && share?.allocatedTransportCost != null && share.allocatedTransportCost > 0 && share.allocationPercent != null) {
        return t('transport:attach.successCost', {
          share: formatLocalizedPercent(share.allocationPercent, i18n.language, 2),
          amount: formatMoneyPl(share.allocatedTransportCost, 'PLN'),
        });
      }
      return t('transport:attach.successCostRecalculated');
    }
    return t('transport:attach.successCostInbound');
  };

  const attach = async (transportId: string, target: AttachTargetRef): Promise<TransportAttachResult | null> => {
    setSaving(true);
    try {
      const result = await attachOrderToTransportRoute(
        transportId,
        target.type === 'order' ? { orderId: target.id } : { supplierOrderId: target.id },
        { modal, t },
      );
      if (!result) return null;
      // Round 3 (B2): the whole new split of the truck and its key.
      const shares = canViewCosts && result.route.direction !== 'INBOUND' ? describeRemainingShares(result.route) : '';
      const split = shares
        ? t('transport:attach.successSplit', { split: shares, key: routeAllocationKeyLabel(result.route, t) })
        : '';
      feedback.success(
        <span data-testid="transport-attach-success">
          {t('transport:attach.success', { order: target.label, truck: getTransportLabel(result.route) })}
          {' '}
          {describe(target, result)}
          {split ? <>{' '}{split}</> : null}
        </span>,
        { duration: ATTACH_SUCCESS_DURATION_SECONDS, key: ATTACH_FEEDBACK_KEYS[0] },
      );
      // The same cost-split warnings as a form save (a key that cannot weigh
      // the new goods gives them 0 — the operator must know).
      showTransportSaveWarnings(result.raw, feedback, t, canViewCosts);
      if (target.type === 'order' && result.route.costAllocationMethod === 'MANUAL' && result.route.direction !== 'INBOUND') {
        feedback.warning(
          <span data-testid="transport-attach-manual-warning">
            {t('transport:attach.manualKeyWarning', { order: target.label })}
            {' '}
            <Link
              to={`/app/transports/${encodeURIComponent(result.route.id)}/edit`}
              onClick={() => dismissAttachFeedback(feedback.message)}
            >
              {t('transport:attach.manualKeyLink')}
            </Link>
          </span>,
          { sticky: true, key: ATTACH_FEEDBACK_KEYS[1] },
        );
      }
      return result;
    } catch (error) {
      feedback.error(getTransportErrorMessage(error, t, 'transport:attach.error'), {
        sticky: isBlockingTransportError(error),
      });
      return null;
    } finally {
      setSaving(false);
    }
  };

  return { attach, saving };
}
